<template>
  <form
    v-if="isPermittedAll([
      'CustomersGetAll'
    ])"
    id="customers-search-form"
    @submit.prevent="searchCustomers">
    <v-container class="px-5">
      <!-- Customer Type -->
      <v-row>
        <v-col class="pt-0">
          <h3 class="mb-3">Filter</h3>
          <label for="customers-search-customer-type">Customer Type:</label>
          <Autocomplete
            id="customers-search-support-customer-type"
            :items="$store.state.customerTypes"
            v-model="customerTypeSearchParameter"
            item-value="CustomerTypeID"
            item-text="CustomerTypeName"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Customer Type -->

      <!-- Contract End Before -->
      <v-row>
        <v-col>
          <label for="customers-search-contract-end">Contract End Before:</label>
          <DatePicker
            input-id="customers-search-contract-end"
            :line-bottom="false"
            v-model="customerSearchParameters.ContractEndBeforeUtc"
            @input="$store.commit(
              'setSearchParameters',
              { params: {
                ContractEndBeforeUtc: customerSearchParameters.ContractEndBeforeUtc,
              }, paramsKey: 'customerSearchParameters' },
            )"
          />
        </v-col>
      </v-row>
      <!-- / Contract End Before -->

      <!-- Support Renewal Declined -->
      <v-row>
        <v-col>
          <label for="search-customers-support-renewal-declined">Support Renewal:</label>
          <Autocomplete
            id="search-customers-support-renewal-declined"
            :items="$store.state.supportRenewalStatuses"
            v-model="supportRenewalStatusSearchParameter"
            item-value="SupportRenewalDeclined"
            item-text="SupportRenewalStatus"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Support Renewal Declined -->

      <!-- Customer Status -->
      <v-row>
        <v-col>
          <label for="search-customers-status">Customer Status:</label>
          <Autocomplete
            id="search-customers-status"
            :items="$store.state.customerStatuses"
            v-model="customerStatusSearchParameter"
            item-value="CustomerStatusColorName"
            item-text="CustomerStatusColorDescription"
            outlined
            filled
            include-any>
            <template v-slot:item="{ item }">
              <div class="w-100 d-flex align-center">
                <div class="mr-auto">{{item.CustomerStatusColorDescription}}</div>
                <span
                  class="customer-status-dot ml-3"
                  :class="item.CustomerStatusColorName">
                </span>
              </div>
            </template>
            <template v-slot:selection="{ item }">
              <div class="w-100 d-flex align-center">
                <div class="text-truncate mr-auto">{{item.CustomerStatusColorDescription}}</div>
                <span
                  class="customer-status-dot"
                  :class="item.CustomerStatusColorName">
                </span>
              </div>
            </template>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Customer Status -->

<!-- Customer ProductKey -->
      <v-row
        v-if="isPermittedAll([
          'ComputersGetAll',
        ])">
        <v-col>
          <label for="search-product-key">Product Key:</label>
          <Autocomplete
            id="search-product-key"
            :items="$store.getters.productKeys"
            v-model="productKeySearchParameter"
            item-text="ProductKeyName"
            item-value="ProductKey"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Customer ProductKey -->

      <!-- Customer EmployeeName -->
      <v-row
        v-if="isPermittedAll([
          'EmployeesGetAll',
        ])">
        <v-col>
          <label for="search-employee-name">Employee Name:</label>
          <Autocomplete
            id="search-employee-name"
            :items="$store.state.employees"
            v-model="employeeSearchParameter"
            item-value="EmployeeID"
            item-text="EmployeeName"
            outlined
            filled
            include-any>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Customer EmployeeName -->

      <!-- Courtesy Call -->
      <v-row>
        <v-col>
          <v-checkbox
            v-model="customerSearchParameters.CourtesyCall"
            @change="$store.commit(
              'setSearchParameters',
              { params: {
                CourtesyCall: customerSearchParameters.CourtesyCall,
              }, paramsKey: 'customerSearchParameters' },
            )"
            label="Courtesy Call"
            class="mt-0 pt-0"
            hide-details>
          </v-checkbox>
        </v-col>
      </v-row>
      <!-- / Courtesy Call -->

      <!-- Hidden -->
      <v-row
        v-if="isPermittedAll([
          'CustomersViewDeleted'
        ])">
        <v-col>
          <label for="search-customers-include-hidden">Hidden:</label>
          <Autocomplete
            id="search-customers-include-hidden"
            :items="$store.state.hiddenStatuses"
            v-model="hiddenStatusSearchParameter"
            outlined
            filled>
          </Autocomplete>
        </v-col>
      </v-row>
      <!-- / Hidden -->

      <!-- Search -->
      <v-row>
        <v-col>
          <v-btn
            type="submit"
            color="success"
            outlined
            block
            :disabled="$store.getters.isLoadingActive">
            Search Customers
          </v-btn>
        </v-col>
      </v-row>
      <!-- / Search -->
    </v-container>
  </form>
</template>

<script>
import _ from 'lodash';
import DatePicker from './DatePicker.vue';

export default {
  name: 'CustomersSearchForm',
  components: {
    DatePicker,
  },
  computed: {
    /* Parameters */
    customerTypeSearchParameter: {
      get() {
        let customerType = _.find(
          this.$store.state.customerTypes,
          { CustomerTypeID: this.customerSearchParameters.CustomerTypeID },
        );

        if (!customerType) {
          customerType = {
            CustomerTypeID: undefined,
            CustomerTypeName: this.anyOption,
          };
        }

        return _.pick(customerType, ['CustomerTypeID', 'CustomerTypeName']);
      },
      set(customerTypeSearchParameter) {
        const params = {
          CustomerTypeID: customerTypeSearchParameter.CustomerTypeID || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    supportRenewalStatusSearchParameter: {
      get() {
        let supportStatus = _.find(
          this.$store.state.supportRenewalStatuses,
          { SupportRenewalDeclined: this.customerSearchParameters.SupportRenewalDeclined },
        );

        if (!supportStatus) {
          supportStatus = {
            SupportRenewalDeclined: undefined,
            SupportRenewalStatus: this.anyOption,
          };
        }

        return _.pick(supportStatus, ['SupportRenewalDeclined', 'SupportRenewalStatus']);
      },
      set(supportRenewalStatusSearchParameter) {
        const params = {
          SupportRenewalDeclined: undefined,
        };

        const status = supportRenewalStatusSearchParameter.SupportRenewalDeclined;

        if (typeof status === 'boolean') {
          params.SupportRenewalDeclined = status;
        }

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    productKeySearchParameter: {
      get() {
        let productKey = _.find(
          this.$store.getters.productKeys,
          { ProductKey: this.customerSearchParameters.ProductKey },
        );

        if (!productKey) {
          productKey = {
            ProductKey: undefined,
            ProductKeyName: this.anyOption,
          };
        }

        return _.pick(productKey, ['ProductKey', 'ProductKeyName']);
      },
      set(productKeySearchParameter) {
        const params = {
          ProductKey: productKeySearchParameter.ProductKey || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    employeeSearchParameter: {
      get() {
        let employee = _.find(
          this.$store.state.employees,
          { EmployeeID: this.customerSearchParameters.EmployeeID,
            EmployeeName: this.customerSearchParameters.EmployeeName },
        );

        if (!employee) {
          employee = {
            EmployeeID: undefined,
            EmployeeName: this.anyOption,
          };
        }

        return _.pick(employee, ['EmployeeID', 'EmployeeName']);
      },
      set(employeeSearchParameter) {
        const params = { EmployeeID: undefined, EmployeeName: undefined};

        if (employeeSearchParameter.EmployeeID) {
          params.EmployeeName = employeeSearchParameter.EmployeeName;
          params.EmployeeID = employeeSearchParameter.EmployeeID;
        }

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    customerStatusSearchParameter: {
      get() {
        let customerStatus = _.find(
          this.$store.state.customerStatuses,
          { CustomerStatusColorName: this.customerSearchParameters.CustomerStatusColorName },
        );

        if (!customerStatus) {
          customerStatus = {
            CustomerStatusColorName: undefined,
            CustomerStatusColorDescription: this.anyOption,
          };
        }

        return _.pick(customerStatus, ['CustomerStatusColorName', 'CustomerStatusColorDescription']);
      },
      set(customerStatusSearchParameter) {
        const params = {
          CustomerStatusColorName: customerStatusSearchParameter.CustomerStatusColorName
            || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    hiddenStatusSearchParameter: {
      get() {
        let hiddenStatus = _.find(
          this.$store.state.hiddenStatuses,
          { value: this.customerSearchParameters.HiddenStatus },
        );

        if (!hiddenStatus) {
          hiddenStatus = {
            value: undefined,
            text: this.anyOption,
          };
        }

        return hiddenStatus;
      },
      set(hiddenStatusSearchParameter) {
        const params = {
          HiddenStatus: hiddenStatusSearchParameter.value || undefined,
        };

        this.$store.commit(
          'setSearchParameters',
          { paramsKey: 'customerSearchParameters', params },
        );
      },
    },
    /* / Parameters */

    customerSearchParameters() {
      return this.$store.state.customerSearchParameters;
    },
  },
  methods: {
    searchCustomers() {
      this.$store.dispatch('getCustomers');
    },
  },
  created() {
    this.$store.dispatch('getCustomerTypes');
    this.$store.dispatch('getCustomerStatuses');
    this.$store.dispatch('getComputers');
    this.$store.dispatch('getEmployees');
  },
};
</script>

<style lang="scss"></style>
